/**
 * Centralized Error Handling Utilities
 * Provides consistent error handling across the application
 */

// Error types
export const ERROR_TYPES = {
    NETWORK: 'NETWORK_ERROR',
    VALIDATION: 'VALIDATION_ERROR',
    AUTHENTICATION: 'AUTHENTICATION_ERROR',
    AUTHORIZATION: 'AUTHORIZATION_ERROR',
    NOT_FOUND: 'NOT_FOUND_ERROR',
    SERVER: 'SERVER_ERROR',
    UNKNOWN: 'UNKNOWN_ERROR'
};

// Error severity levels
export const ERROR_SEVERITY = {
    LOW: 'low',
    MEDIUM: 'medium',
    HIGH: 'high',
    CRITICAL: 'critical'
};

/**
 * Parse API error response
 * @param {Error} error - Axios error object
 * @returns {Object} Parsed error object
 */
export const parseApiError = (error) => {
    const baseError = {
        type: ERROR_TYPES.UNKNOWN,
        severity: ERROR_SEVERITY.MEDIUM,
        message: 'An unexpected error occurred',
        details: null,
        timestamp: new Date().toISOString(),
        originalError: error
    };

    if (error.response) {
        // Server responded with error status
        const { status, data } = error.response;

        switch (status) {
            case 400:
                return {
                    ...baseError,
                    type: ERROR_TYPES.VALIDATION,
                    severity: ERROR_SEVERITY.LOW,
                    message: data?.detail || 'Invalid request data',
                    details: data
                };

            case 401:
                return {
                    ...baseError,
                    type: ERROR_TYPES.AUTHENTICATION,
                    severity: ERROR_SEVERITY.HIGH,
                    message: 'Authentication required',
                    details: data
                };

            case 403:
                return {
                    ...baseError,
                    type: ERROR_TYPES.AUTHORIZATION,
                    severity: ERROR_SEVERITY.HIGH,
                    message: 'Insufficient permissions',
                    details: data
                };

            case 404:
                return {
                    ...baseError,
                    type: ERROR_TYPES.NOT_FOUND,
                    severity: ERROR_SEVERITY.MEDIUM,
                    message: 'Resource not found',
                    details: data
                };

            case 422:
                return {
                    ...baseError,
                    type: ERROR_TYPES.VALIDATION,
                    severity: ERROR_SEVERITY.MEDIUM,
                    message: 'Validation failed',
                    details: data
                };

            case 500:
                return {
                    ...baseError,
                    type: ERROR_TYPES.SERVER,
                    severity: ERROR_SEVERITY.CRITICAL,
                    message: 'Internal server error',
                    details: data
                };

            default:
                return {
                    ...baseError,
                    type: ERROR_TYPES.SERVER,
                    severity: ERROR_SEVERITY.HIGH,
                    message: `Server error (${status})`,
                    details: data
                };
        }
    } else if (error.request) {
        // Network error
        return {
            ...baseError,
            type: ERROR_TYPES.NETWORK,
            severity: ERROR_SEVERITY.HIGH,
            message: 'Network connection failed',
            details: {
                url: error.config?.url,
                method: error.config?.method,
                timeout: error.config?.timeout
            }
        };
    } else {
        // Request setup error
        return {
            ...baseError,
            type: ERROR_TYPES.UNKNOWN,
            severity: ERROR_SEVERITY.MEDIUM,
            message: error.message || 'Request setup failed',
            details: { message: error.message }
        };
    }
};

/**
 * Get user-friendly error message
 * @param {Object} parsedError - Parsed error object
 * @returns {string} User-friendly message
 */
export const getUserFriendlyMessage = (parsedError) => {
    const { type, message, details } = parsedError;

    switch (type) {
        case ERROR_TYPES.NETWORK:
            return 'Unable to connect to the server. Please check your internet connection and try again.';

        case ERROR_TYPES.AUTHENTICATION:
            return 'Your session has expired. Please log in again.';

        case ERROR_TYPES.AUTHORIZATION:
            return 'You do not have permission to perform this action.';

        case ERROR_TYPES.NOT_FOUND:
            return 'The requested resource was not found.';

        case ERROR_TYPES.VALIDATION:
            if (details?.errors) {
                const errorMessages = Object.values(details.errors).flat();
                return `Please fix the following errors: ${errorMessages.join(', ')}`;
            }
            return message || 'Please check your input and try again.';

        case ERROR_TYPES.SERVER:
            return 'A server error occurred. Please try again later or contact support if the problem persists.';

        default:
            return message || 'An unexpected error occurred. Please try again.';
    }
};

/**
 * Error handler class for consistent error management
 */
export class ErrorHandler {
    constructor() {
        this.errorLog = [];
        this.maxLogSize = 100;
    }

    /**
     * Handle and log error
     * @param {Error} error - Error object
     * @param {Object} context - Additional context
     * @returns {Object} Parsed error
     */
    handle(error, context = {}) {
        const parsedError = parseApiError(error);
        const userMessage = getUserFriendlyMessage(parsedError);

        // Add context
        parsedError.context = context;
        parsedError.userMessage = userMessage;

        // Log error
        this.logError(parsedError);

        // Send to error reporting service in production
        if (process.env.NODE_ENV === 'production') {
            this.reportError(parsedError);
        }

        return parsedError;
    }

    /**
     * Log error to console and internal log
     * @param {Object} parsedError - Parsed error object
     */
    logError(parsedError) {
        const { type, severity, message, details, context } = parsedError;

        // Console logging based on severity
        const logMethod = this.getLogMethod(severity);
        logMethod(`[${type}] ${message}`, {
            severity,
            details,
            context,
            timestamp: parsedError.timestamp
        });

        // Add to internal log
        this.errorLog.unshift(parsedError);
        if (this.errorLog.length > this.maxLogSize) {
            this.errorLog = this.errorLog.slice(0, this.maxLogSize);
        }
    }

    /**
     * Get appropriate console log method based on severity
     * @param {string} severity - Error severity
     * @returns {Function} Console log method
     */
    getLogMethod(severity) {
        switch (severity) {
            case ERROR_SEVERITY.CRITICAL:
                return console.error;
            case ERROR_SEVERITY.HIGH:
                return console.error;
            case ERROR_SEVERITY.MEDIUM:
                return console.warn;
            case ERROR_SEVERITY.LOW:
                return console.info;
            default:
                return console.log;
        }
    }

    /**
     * Report error to external service
     * @param {Object} parsedError - Parsed error object
     */
    reportError(parsedError) {
        // TODO: Implement error reporting service integration
        // Examples: Sentry, LogRocket, Bugsnag, etc.
        console.log('Reporting error to external service:', parsedError);
    }

    /**
     * Get recent errors
     * @param {number} limit - Number of errors to return
     * @returns {Array} Recent errors
     */
    getRecentErrors(limit = 10) {
        return this.errorLog.slice(0, limit);
    }

    /**
     * Clear error log
     */
    clearLog() {
        this.errorLog = [];
    }
}

// Create global error handler instance
export const errorHandler = new ErrorHandler();

/**
 * React hook for error handling
 * @param {Function} onError - Error callback function
 * @returns {Function} Error handler function
 */
export const useErrorHandler = (onError) => {
    return (error, context = {}) => {
        const parsedError = errorHandler.handle(error, context);
        if (onError) {
            onError(parsedError);
        }
        return parsedError;
    };
};

/**
 * Higher-order component for error handling
 * @param {React.Component} Component - Component to wrap
 * @returns {React.Component} Wrapped component with error handling
 */
export const withErrorHandling = (Component) => {
    return (props) => {
        const handleError = useErrorHandler();

        return <Component {...props} onError={handleError} />;
    };
};

/**
 * Utility function for async error handling
 * @param {Function} asyncFn - Async function to execute
 * @param {Object} context - Error context
 * @returns {Promise} Promise with error handling
 */
export const withErrorHandlingAsync = async (asyncFn, context = {}) => {
    try {
        return await asyncFn();
    } catch (error) {
        throw errorHandler.handle(error, context);
    }
};

export default errorHandler;
